import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { Alert, Form, Col, Row, Button, Card, Spinner } from 'react-bootstrap';
import api from '../../services/api';
import AppFooter from '../../components/AppFooter';
import { receitaValidationSchema } from './receitaSchema';

const INITIAL_FORM = (params => ({
  codReceita: params.get('receita') ?? '',
  codPaciente: params.get('paciente') ?? '',
  cpfUpdate: '',
  origem: '',
}))(new URLSearchParams(window.location.search));

export default function ValidaReceita() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [status, setStatus] = useState({ kind: 'idle' }); // 'idle', 'success', 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = isSubmitting || status.kind === 'success';

  const handleClickICM = event => {
    event.preventDefault();
    window.location.href = '//www.icm.com.br';
  };

  const handleChange = field => event => {
    const raw = event.target.value;
    const value = field === 'cpfUpdate' ? raw.replace(/\D/g, '') : raw;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = payload => {
    try {
      receitaValidationSchema.parse(payload);
      setFormErrors({});
      return true;
    } catch (error) {
      if (error.errors) {
        const errors = {};
        error.errors.forEach(err => {
          errors[err.path[0]] = err.message;
        });
        setFormErrors(errors);
      }
      return false;
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const payload = { ...form, status: 'Baixada' };

    if (!validateForm(payload)) return;

    setIsSubmitting(true);
    setStatus({ kind: 'idle' });

    try {
      await api.put(`/paciente/receita/${payload.codReceita}`, payload);
      setStatus({ kind: 'success' });
    } catch (err) {
      const serverMessage = err?.response?.data?.message;

      if (serverMessage) {
        setStatus({ kind: 'error', message: serverMessage });
        return;
      }

      if (err?.request && !err?.response) {
        setStatus({
          kind: 'error',
          message: 'Erro no servidor! Verifique sua conexão.',
        });
        return;
      }

      setStatus({ kind: 'error', message: 'Erro ao validar a receita!' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Row className="justify-content-md-center mt-4">
      <Col md="auto">
        <Card border="success" className="text-center">
          <Card.Header>
            <Card.Title className="text-success font-weight-bold">
              VALIDAÇÃO DE RECEITA
            </Card.Title>
            {status.kind === 'success' && (
              <Alert variant="success">Receita VALIDADA com Sucesso!</Alert>
            )}
            {status.kind === 'error' && (
              <Alert variant="danger">{status.message}</Alert>
            )}
          </Card.Header>

          <Card.Body>
            <Card.Text className="text-success">
              <strong>Informe os dados abaixo para VALIDAR a receita!</strong>
            </Card.Text>
            <Form onSubmit={handleSubmit}>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputReceita">
                    <strong>CÓDIGO DA RECEITA</strong>
                  </Form.Label>
                  <Form.Control
                    className="text-center"
                    id="inputReceita"
                    name="codReceita"
                    readOnly
                    value={form.codReceita}
                    aria-describedby="receitaHelp"
                    isInvalid={!!formErrors.codReceita}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.codReceita}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputPaciente">
                    <strong>CÓDIGO DO PACIENTE</strong>
                  </Form.Label>
                  <Form.Control
                    className="text-center"
                    id="inputPaciente"
                    name="codPaciente"
                    readOnly
                    value={form.codPaciente}
                    aria-describedby="pacienteHelp"
                    isInvalid={!!formErrors.codPaciente}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.codPaciente}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputCPF">
                    <strong>CPF DO PROFISSIONAL</strong>
                  </Form.Label>
                  <Form.Control
                    className="text-center"
                    type="text"
                    id="inputCPF"
                    name="cpfUpdate"
                    placeholder="Digite apenas números"
                    maxLength={11}
                    inputMode="numeric"
                    value={form.cpfUpdate}
                    onChange={handleChange('cpfUpdate')}
                    disabled={isDisabled}
                    isInvalid={!!formErrors.cpfUpdate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.cpfUpdate}
                  </Form.Control.Feedback>
                  <Form.Text id="cpfHelp" muted>
                    <div className="text-primary">
                      Informe o CPF do Profissional da Farmácia que está
                      realizando a Baixa da Receita.
                    </div>
                  </Form.Text>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputFarmacia">
                    <strong>FARMÁCIA (Nome Fantasia)</strong>
                  </Form.Label>
                  <Form.Control
                    className="text-center"
                    type="text"
                    id="inputFarmacia"
                    name="origem"
                    placeholder="Nome da farmácia"
                    value={form.origem}
                    onChange={handleChange('origem')}
                    disabled={isDisabled}
                    isInvalid={!!formErrors.origem}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.origem}
                  </Form.Control.Feedback>
                  <Form.Text id="farmaciaHelp" muted>
                    <div className="text-primary">
                      Informe o Nome Fantasia da Farmácia onde está sendo
                      realizada a compra.
                    </div>
                  </Form.Text>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm className="text-center">
                  <Button type="submit" size="lg" disabled={isDisabled}>
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      'Validar'
                    )}
                  </Button>{' '}
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    onClick={handleClickICM}
                    disabled={isSubmitting}
                  >
                    Ir para o ICM
                  </Button>
                </Form.Group>
              </Form.Row>
            </Form>
          </Card.Body>
          <Card.Footer className="text-muted">
            <div>
              <QRCode size={50} value="https://www.moraes-tech.com/" />
            </div>
            <AppFooter />
          </Card.Footer>
        </Card>
      </Col>
    </Row>
  );
}
